/** Client portal: invoices, signing, organizers and the refund tracker. */
import { GroupInput, SensitiveInput } from "@/components/form-inputs";
import { askText } from "@/components/dialogs";
import {
  fillYear,
  initialsOf,
  formatCents,
  missingRequired,
  organizerDefinitionSchema,
  parseDollars,
  rowsOf,
  visibleQuestions,
  type AnswerValue,
  type Answers,
  type Question,
} from "@clientflow/shared";
import { useMutation, useQuery } from "@tanstack/react-query";
import { BadgeCheck, CheckCircle2, Clock, Copy, CreditCard, ExternalLink, FileSignature, ListChecks, Phone } from "lucide-react";
import { useMemo, useState } from "react";
import { Link, useLocation, useSearch } from "wouter";
import { InvoiceStatus, METHOD_LABELS, PortalCreditNote, PortalInvoiceEventRows, type InvoiceDetail, type InvoiceSummary } from "@/components/billing";
import type { ClientInvoiceEvent } from "@clientflow/shared";
import { Empty, Loading, PageHeader, Section } from "@/components/page";
import { PortalPaymentMethods, PortalProposalsSection, ProposalTodoCards, useProposalsToAccept } from "@/pages/portal-lifecycle";
import { boxStyle, PdfPages } from "@/components/pdf-view";
import { UploadButton } from "@/components/panels";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Progress } from "@/components/ui/progress";
import { Textarea } from "@/components/ui/textarea";
import { api, ApiError, errorMessage } from "@/lib/api";
import { formatDate } from "@/lib/format";
import { useToast } from "@/lib/use-toast";

// --- To-do summary for the home page ----------------------------------------------------

export type FeeApproval = {
  id: string;
  clientId: string;
  clientName: string;
  number: number | null;
  totalCents: number;
  memo: string | null;
  engagementName: string | null;
  partnerName: string | null;
  requestedAt: string | null;
  approvedAt: string | null;
  approvedName: string | null;
  declinedAt: string | null;
  lines: Array<{ description: string; quantity: number; amountCents: number }>;
};

type SigSummary = { id: string; clientId: string; title: string; status: string; myStatus: string; method: string; dueOn: string | null };
type OrgSummary = { id: string; clientId: string; kind?: "organizer" | "intake"; title: string; status: string; progress: { answered: number; total: number } };

/** The entity an item belongs to, shown only to people linked to more than one. */
function useEntityName() {
  const { data } = useQuery<{ clients: Array<{ id: string; displayName: string }> }>({ queryKey: ["/api/portal/home"] });
  return (id: string) => (data && data.clients.length > 1 ? data.clients.find((c) => c.id === id)?.displayName : undefined);
}

export function usePortalTodos() {
  const sigs = useQuery<SigSummary[]>({ queryKey: ["/api/portal/signatures"] });
  const invoices = useQuery<InvoiceSummary[]>({ queryKey: ["/api/portal/invoices"] });
  const organizers = useQuery<OrgSummary[]>({ queryKey: ["/api/portal/organizers"] });
  const toSign = (sigs.data ?? []).filter((s) => s.status === "pending" && s.myStatus === "pending");
  // A fee taken from the refund isn't paid here; it's approved instead.
  const toPay = (invoices.data ?? []).filter((i) => i.status === "open" && !(i as InvoiceSummary & { paidFromRefund?: boolean }).paidFromRefund);
  const approvals = useQuery<FeeApproval[]>({ queryKey: ["/api/portal/fee-approvals"] });
  const toApprove = approvals.data ?? [];
  const toAnswer = (organizers.data ?? []).filter((o) => o.status === "open");
  // Transactions waiting for the client's description (the card on the home page).
  const questions = useQuery<Array<{ questions: Array<{ status: string }> }>>({ queryKey: ["/api/portal/books/questions"] });
  const toDescribe = (questions.data ?? []).reduce((n, c) => n + c.questions.filter((q) => q.status === "open").length, 0);
  const toAccept = useProposalsToAccept();
  return { toSign, toPay, toAnswer, toApprove, toDescribe, toAccept, count: toSign.length + toPay.length + toAnswer.length + toApprove.length + toAccept.length + (toDescribe ? 1 : 0) };
}

export function PortalTodoCards() {
  const { toSign, toPay, toAnswer, toApprove, toAccept } = usePortalTodos();
  const nameOf = useEntityName();
  const forWhom = (id: string) => (nameOf(id) ? <span className="block text-xs text-muted-foreground">For {nameOf(id)}</span> : null);
  if (!toSign.length && !toPay.length && !toAnswer.length && !toApprove.length && !toAccept.length) return null;
  const item = "flex items-center gap-3 rounded-xl border border-card-border bg-card p-4 shadow-xs hover:border-primary/50";
  return (
    <>
    <ProposalTodoCards />
    <ul className="mb-3 space-y-3">
      {toAnswer.map((o) => (
        <li key={o.id}>
          <Link href={`/organizers/${o.id}`} className={item}>
            <ListChecks className="h-5 w-5 text-primary" />
            <span className="flex-1">
              <span className="block font-medium">{o.title}</span>
              {forWhom(o.clientId)}
              <span className="text-sm text-muted-foreground">
                {o.progress.answered
                  ? `${o.progress.answered} of ${o.progress.total} questions answered. Continue where you left off.`
                  : o.kind === "intake"
                    ? "Welcome. Please complete your client intake form so that we can begin our work together."
                    : "Please complete your tax organizer. Your answers determine the documents we will request."}
              </span>
            </span>
          </Link>
        </li>
      ))}
      {toApprove.map((f) => (
        <li key={f.id}>
          <Link href={`/fees/${f.id}`} className={item}>
            <BadgeCheck className="h-5 w-5 text-primary" />
            <span className="flex-1">
              <span className="block font-medium">Approve your fee — {formatCents(f.totalCents)}</span>
              {forWhom(f.clientId)}
              <span className="text-sm text-muted-foreground">To be taken from your refund{f.partnerName ? ` by ${f.partnerName}` : ""}. Please review and approve it.</span>
            </span>
          </Link>
        </li>
      ))}
      {toSign.map((s) => (
        <li key={s.id}>
          <Link href={`/sign/${s.id}`} className={item}>
            <FileSignature className="h-5 w-5 text-primary" />
            <span className="flex-1">
              <span className="block font-medium">Signature required: {s.title}</span>
              {forWhom(s.clientId)}
              {s.dueOn && <span className="text-sm text-muted-foreground">Due {formatDate(s.dueOn)}</span>}
            </span>
          </Link>
        </li>
      ))}
      {toPay.map((i) => (
        <li key={i.id}>
          <Link href={`/invoices/${i.id}`} className={item}>
            <CreditCard className="h-5 w-5 text-primary" />
            <span className="flex-1">
              <span className="block font-medium">
                Pay invoice #{i.number} — {formatCents(i.balanceCents ?? i.totalCents - i.amountPaidCents)}
              </span>
              {forWhom(i.clientId)}
              {i.dueOn && <span className="text-sm text-muted-foreground">Due {formatDate(i.dueOn)}</span>}
            </span>
          </Link>
        </li>
      ))}
    </ul>
    </>
  );
}

// --- Invoices -------------------------------------------------------------------------------

export function PortalInvoicesPage() {
  const { data, isLoading } = useQuery<InvoiceSummary[]>({ queryKey: ["/api/portal/invoices"] });
  const nameOf = useEntityName();
  const { data: home } = useQuery<{ clients: Array<{ id: string; displayName: string; access: string }> }>({ queryKey: ["/api/portal/home"] });
  const fullClients = (home?.clients ?? []).filter((c) => c.access === "full");
  return (
    <>
      <PageHeader title="Invoices & Payments" description="Invoices issued by your firm, payments received, proposals, and automatic payment." />
      <div className="space-y-6">
      <PortalProposalsSection />
      <PortalCreditNote />
      <Section title="Invoices">
      {isLoading ? (
        <Loading />
      ) : !data?.length ? (
        <Empty>No invoices have been issued. Invoices from your firm will appear here.</Empty>
      ) : (
        <ul className="-my-2 divide-y divide-border">
          {data.map((i) => (
            <li key={i.id}>
              <Link href={`/invoices/${i.id}`} className="flex items-center gap-3 py-3 hover:text-primary">
                <span className="w-20 font-medium">#{i.number}</span>
                <span className="min-w-0 flex-1 text-sm text-muted-foreground">
                  {formatDate(i.issuedOn)}
                  {nameOf(i.clientId) && <span className="block truncate text-xs">{nameOf(i.clientId)}</span>}
                </span>
                <span className="text-right">
                  <span className="block font-semibold tabular">{formatCents(i.totalCents)}</span>
                  {i.status === "open" && i.balanceCents !== undefined && i.balanceCents !== i.totalCents && <span className="block whitespace-nowrap text-xs text-muted-foreground tabular">{formatCents(i.balanceCents)} due</span>}
                </span>
                {(i as InvoiceSummary & { paidFromRefund?: boolean }).paidFromRefund && i.status === "open" ? (
                  <span className="rounded-full bg-secondary px-2.5 py-0.5 text-xs font-medium text-muted-foreground">From your refund</span>
                ) : (
                  <InvoiceStatus status={i.status} dueOn={i.dueOn} />
                )}
              </Link>
            </li>
          ))}
        </ul>
      )}
      </Section>
      {fullClients.map((c) => (
        <PortalPaymentMethods key={c.id} clientId={c.id} clientName={fullClients.length > 1 ? c.displayName : undefined} />
      ))}
      </div>
    </>
  );
}

export function PortalInvoicePage({ id }: { id: string }) {
  const search = new URLSearchParams(useSearch());
  const returned = search.get("checkout");
  const { data, isLoading } = useQuery<Omit<InvoiceDetail, "events"> & { onlinePayment: boolean; events?: ClientInvoiceEvent[] }>({
    queryKey: ["/api/portal/invoices/", id],
    // After returning from checkout, watch for the payment to be confirmed.
    refetchInterval: (q) => (returned === "success" && q.state.data?.invoice.status === "open" ? 4000 : false),
  });
  const pay = useMutation({
    meta: { inlineError: true },
    mutationFn: () => api.send<{ url: string }>("POST", `/api/portal/invoices/${id}/checkout`),
    onSuccess: (r) => {
      window.location.href = r.url;
    },
  });
  if (isLoading) return <Loading />;
  if (!data) return <Empty>Invoice not found.</Empty>;
  const inv = data.invoice;
  return (
    <>
      <PageHeader back={{ label: "Invoices & Payments", href: "/invoices" }} title={`Invoice #${inv.number}`} description={`Issued ${formatDate(inv.issuedOn)}${inv.dueOn ? ` · Due ${formatDate(inv.dueOn)}` : ""}`} actions={<InvoiceStatus status={inv.status} dueOn={inv.dueOn} />} />
      {returned === "success" && inv.status === "open" && (
        <p className="mb-4 flex items-center gap-2 rounded-lg border border-primary/40 bg-primary/5 px-4 py-3 text-sm">
          <Clock className="h-4 w-4" /> Thank you. We're confirming your payment. Bank payments can take a few business days.
        </p>
      )}
      {returned === "cancelled" && inv.status === "open" && <p className="mb-4 rounded-lg border border-border px-4 py-3 text-sm">Payment was cancelled. No charge was made.</p>}
      {inv.status === "open" && inv.autopayStatus === "processing" && <p className="mb-4 flex items-center gap-2 rounded-lg border border-primary/40 bg-primary/5 px-4 py-3 text-sm"><Clock className="h-4 w-4" /> Your automatic payment is being processed. A bank payment can take a few business days.</p>}
      {inv.status === "open" && inv.autopayStatus === "failed" && (
        <p className="mb-4 rounded-lg border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-900 dark:border-red-900 dark:bg-red-950/40 dark:text-red-200">
          The automatic payment did not go through{inv.autopayError ? ` (${inv.autopayError})` : ""}.{inv.autopayNextAttemptOn ? ` It will be tried once more on ${formatDate(inv.autopayNextAttemptOn)}.` : ""} You can pay below, or update your payment method on{" "}
          <Link href="/invoices" className="font-medium underline">Invoices & Payments</Link>.
        </p>
      )}
      <Section title="Details">
        <table className="w-full text-sm">
          <tbody>
            {data.lines.map((l) => (
              <tr key={l.id} className="border-b border-border/60">
                <td className="py-2">
                  {l.description}
                  {l.quantity > 1 && <span className="text-muted-foreground"> × {l.quantity}</span>}
                </td>
                <td className="py-2 text-right">{formatCents(l.amountCents)}</td>
              </tr>
            ))}
            {/* A credit applied shows once, as its own line below. */}
            {data.payments.filter((p) => p.method !== "credit").map((p) => (
              <tr key={p.id} className="text-muted-foreground">
                <td className="py-1">
                  Paid · {METHOD_LABELS[p.method] ?? p.method} · {formatDate(p.receivedOn)}
                </td>
                <td className="whitespace-nowrap py-1 text-right tabular">−{formatCents(p.amountCents)}</td>
              </tr>
            ))}
            <PortalInvoiceEventRows events={data.events ?? []} />
            <tr className="font-semibold">
              <td className="pt-3">Balance due</td>
              <td className="pt-3 text-right">{formatCents(inv.balanceCents)}</td>
            </tr>
          </tbody>
        </table>
        {inv.memo && <p className="mt-3 text-sm text-muted-foreground">{inv.memo}</p>}
        {data.clientCreditCents > 0 && <p className="mt-3 text-sm text-muted-foreground">Your credit with the firm: <span className="font-medium text-foreground tabular">{formatCents(data.clientCreditCents)}</span>, applied to a future invoice.</p>}
        {inv.status === "open" && inv.balanceCents > 0 && (inv as typeof inv & { paidFromRefund?: boolean }).paidFromRefund && (
          <p className="mt-4 flex items-center gap-2 text-sm text-muted-foreground">
            <BadgeCheck className="h-4 w-4 text-primary" /> You approved this fee to be taken from your refund. There's nothing to pay here.
          </p>
        )}
        {inv.status === "open" && inv.balanceCents > 0 && !(inv as typeof inv & { paidFromRefund?: boolean }).paidFromRefund && (
          <div className="mt-4">
            {data.onlinePayment ? (
              <Button onClick={() => pay.mutate()} disabled={pay.isPending}>
                <CreditCard className="mr-2 h-4 w-4" /> Pay {formatCents(inv.balanceCents)} securely
              </Button>
            ) : (
              <p className="text-sm text-muted-foreground">Please contact your firm to arrange payment of this invoice.</p>
            )}
            {pay.error && <p className="mt-2 text-sm text-destructive">{errorMessage(pay.error)}</p>}
          </div>
        )}
        {inv.status === "void" && <p className="mt-4 text-sm text-muted-foreground">Your firm cancelled this invoice. Nothing is due on it; any payment is shown above as refunded or held as credit.</p>}
        {inv.status === "paid" && (
          <p className="mt-4 flex items-center gap-2 text-sm text-emerald-700 dark:text-emerald-300">
            <CheckCircle2 className="h-4 w-4" /> Paid in full. Thank you.
          </p>
        )}
      </Section>
    </>
  );
}

// --- Signing --------------------------------------------------------------------------------

type SigDetail = {
  id: string;
  title: string;
  message: string | null;
  method: "click" | "wet" | "kba";
  status: string;
  reviewNote: string | null;
  signedDocumentId: string | null;
  consentText: string;
  me: { name: string; status: string; viewedAt: string | null };
  signers: Array<{ name: string; status: string }>;
  identityCheck: string;
  today: string;
  fields: Array<{ id: string; page: number; x: number; y: number; width: number; height: number; kind: "signature" | "initials" | "date" | "text"; label: string | null; required: boolean; mine: boolean; signerName: string; done: boolean }>;
};


export function PortalSignPage({ id }: { id: string }) {
  const { toast } = useToast();
  const { data, isLoading, refetch } = useQuery<SigDetail>({ queryKey: ["/api/portal/signatures/", id] });
  const [typed, setTyped] = useState("");
  const [agree, setAgree] = useState(false);
  const [values, setValues] = useState<Record<string, string>>({});
  const [docError, setDocError] = useState<string | null>(null);
  const sign = useMutation({
    meta: { inlineError: true },
    mutationFn: () => api.send<{ status: string }>("POST", `/api/portal/signatures/${id}/sign`, { typedName: typed, agree, values }),
    onSuccess: (r) => toast({ title: "Signature recorded", description: r.status === "completed" ? "All parties have signed. The signed copy is in Documents." : "Awaiting the remaining signer." }),
  });
  const decline = useMutation({ mutationFn: (reason: string) => api.send("POST", `/api/portal/signatures/${id}/decline`, { reason }) });
  const upload = useMutation({
    meta: { inlineError: true },
    mutationFn: (file: File) => api.upload(`/api/portal/signatures/${id}/upload`, {}, file),
    onSuccess: () => toast({ title: "Signed form uploaded", description: "Your firm will review the signed form." }),
  });
  if (isLoading) return <Loading />;
  if (!data) return <Empty>Signature request not found.</Empty>;
  const open = data.status === "pending" && data.me.status === "pending";
  const docUrl = `/api/portal/signatures/${id}/document`;
  const myBoxes = data.fields.filter((f) => f.mine);
  const missingText = myBoxes.filter((f) => f.kind === "text" && f.required && !values[f.id]?.trim()).length;

  return (
    <>
      <PageHeader back={{ label: "home", href: "/" }} title={data.title} description={data.signers.length > 1 ? `Signers: ${data.signers.map((s) => `${s.name}${s.status === "signed" ? " (signed)" : ""}`).join(", ")}` : undefined} />
      <FeeApprovalReminder />
      <Section title={open ? (data.method === "click" ? "Review and sign" : "Print, sign and upload") : "Status"}>
        {data.message && <p className="mb-4 text-sm">{data.message}</p>}
        {data.reviewNote && (
          <p className="mb-4 rounded-md border border-accent/60 bg-accent/10 px-3 py-2 text-sm">
            <span className="font-medium">Note from your firm:</span> {data.reviewNote}
          </p>
        )}
        {data.status === "completed" ? (
          <p className="flex items-center gap-2 text-sm text-emerald-700 dark:text-emerald-300">
            <CheckCircle2 className="h-4 w-4" /> Signed. {data.signedDocumentId && <a className="underline" href={`/api/portal/documents/${data.signedDocumentId}/download`}>Download the signed copy</a>}
          </p>
        ) : data.status === "in_review" ? (
          <p className="text-sm">Your firm is reviewing the signed form you uploaded.</p>
        ) : !open ? (
          <p className="text-sm text-muted-foreground">{data.me.status === "signed" ? "Your signature has been recorded. Awaiting the remaining signer." : "This signature request is closed."}</p>
        ) : data.method === "click" ? (
          <form
            className="space-y-4"
            onSubmit={(e) => {
              e.preventDefault();
              sign.mutate();
            }}
          >
            <p className="text-sm">
              <span className="font-medium">Step 1.</span> Read the document below
              {myBoxes.length > 0 && <> — your {myBoxes.length === 1 ? "box is" : `${myBoxes.length} boxes are`} highlighted</>}.{" "}
              <a href={docUrl} target="_blank" rel="noreferrer" className="text-primary underline">
                Open in a new tab
              </a>
              {data.me.viewedAt && <CheckCircle2 className="ml-1 inline h-4 w-4 text-emerald-600" />}
            </p>
            <div className="max-h-[75vh] overflow-y-auto rounded-md bg-muted/40 p-2">
              <PdfPages
                url={docUrl}
                onLoaded={() => setTimeout(() => refetch(), 300)}
                onError={setDocError}
                overlay={(page) =>
                  data.fields
                    .filter((f) => f.page === page)
                    .map((f) =>
                      f.mine && f.kind === "text" ? (
                        <input
                          key={f.id}
                          aria-label={f.label ?? "Text"}
                          placeholder={f.label ?? "Type here"}
                          value={values[f.id] ?? ""}
                          onChange={(e) => setValues({ ...values, [f.id]: e.target.value })}
                          className="absolute rounded-sm border-2 border-amber-500 bg-amber-50/90 px-1 text-[11px] text-slate-900 outline-hidden focus:border-amber-600"
                          style={boxStyle(f)}
                        />
                      ) : (
                        <div
                          key={f.id}
                          className={`absolute flex items-center overflow-hidden rounded-sm border-2 px-1 text-[11px] ${f.mine ? "border-amber-500 bg-amber-50/90 text-slate-900" : "border-slate-300 bg-slate-100/70 text-slate-500"}`}
                          style={boxStyle(f)}
                          title={f.mine ? undefined : `For ${f.signerName}`}
                        >
                          {!f.mine ? (
                            `${f.signerName.split(" ")[0]}${f.done ? " ✓" : ""}`
                          ) : f.kind === "signature" ? (
                            <span className="truncate font-serif italic">{typed.trim() || "Your signature"}</span>
                          ) : f.kind === "initials" ? (
                            <span className="font-serif italic">{typed.trim() ? initialsOf(typed) || "?" : "Initials"}</span>
                          ) : (
                            <span>{data.today}</span>
                          )}
                        </div>
                      ),
                    )
                }
              />
            </div>
            {data.identityCheck === "waived_by_firm" && (
              <p className="text-xs text-muted-foreground">Your firm has elected to accept electronic signature of this IRS form without online identity verification.</p>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="typed">
                <span className="font-medium">Step 2.</span> Type your full name: <span className="font-semibold">{data.me.name}</span>
              </Label>
              {/* Rev. Proc. 2013-14 §6: the signer types their own name; nothing may fill it in (no default, no browser or password-manager autofill). */}
              <Input
                id="typed"
                name="signature-typed-name"
                autoComplete="off"
                autoCorrect="off"
                spellCheck={false}
                data-1p-ignore
                data-lpignore="true"
                data-form-type="other"
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                className="font-display text-lg"
              />
            </div>
            <label className="flex items-start gap-2 text-sm">
              <input type="checkbox" className="mt-1" checked={agree} onChange={(e) => setAgree(e.target.checked)} />
              <span>{data.consentText}</span>
            </label>
            <div className="flex flex-wrap gap-2">
              <Button type="submit" disabled={!data.me.viewedAt || !agree || !typed.trim() || missingText > 0 || sign.isPending}>
                <FileSignature className="mr-2 h-4 w-4" /> Sign
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={async () => {
                  const reason = await askText({ title: "Decline to sign", label: "Reason for declining (optional, shared with your firm)", confirmLabel: "Decline to sign", destructive: true });
                  if (reason !== null) decline.mutate(reason);
                }}
              >
                Decline to sign
              </Button>
            </div>
            {!data.me.viewedAt && !docError && <p className="text-xs text-muted-foreground">The document is loading…</p>}
            {docError && (
              <p className="text-sm text-muted-foreground">
                {docError}{" "}
                {docError.includes("invoice") && (
                  <Link href="/invoices" className="text-primary underline">
                    See invoices
                  </Link>
                )}
              </p>
            )}
            {missingText > 0 && <p className="text-xs text-muted-foreground">Fill in the highlighted text {missingText === 1 ? "box" : "boxes"} on the document.</p>}
            {sign.error && <p className="text-sm text-destructive">{errorMessage(sign.error)}</p>}
          </form>
        ) : (
          <div className="space-y-3 text-sm">
            <p>The IRS requires this form to be signed by hand unless your identity is verified online.</p>
            <ol className="list-decimal space-y-1 pl-5">
              <li>
                <a href={docUrl} target="_blank" rel="noreferrer" className="text-primary underline">
                  Download the form
                </a>{" "}
                and print it.
              </li>
              <li>Sign and date it{data.signers.length > 1 ? " (every signer)" : ""}.</li>
              <li>Upload a scan or a clear photo of the signed pages.</li>
            </ol>
            <UploadButton onFile={(f) => upload.mutate(f)} disabled={upload.isPending} label={upload.isPending ? "Uploading…" : "Upload signed form"} />
            {upload.error && <p className="text-destructive">{errorMessage(upload.error)}</p>}
          </div>
        )}
      </Section>
    </>
  );
}

/** Dollar amounts with at most two decimals ("1,234.56"); "5000.123" is rejected, not rounded. */
function isMoney(text: string): boolean {
  if (!/^\s*\$?\s*[\d,]*(\.\d{0,2})?\s*$/.test(text) || !/\d/.test(text)) return false;
  try {
    return parseDollars(text) >= 0;
  } catch {
    return false;
  }
}

// --- Organizer --------------------------------------------------------------------------------

type OrganizerView = { id: string; kind?: "organizer" | "intake"; title: string; taxYear: number; status: string; definition: unknown; answers: Answers; fromReturn?: { taxYear: number | null; readAt: string | null; hints: Record<string, string> } | null };

export function QuestionInput({ q, value, onChange, onInvalid, year }: { q: Question; value: AnswerValue | undefined; onChange: (v: AnswerValue | undefined) => void; onInvalid: (bad: boolean) => void; year?: number }) {
  const id = `q-${q.key}`;
  const [moneyText, setMoneyText] = useState(typeof value === "number" && q.type === "money" ? (value / 100).toFixed(2) : "");
  switch (q.type) {
    case "group":
      return <GroupInput q={q} year={year} rows={rowsOf(value)} onChange={(rows) => onChange(rows.length ? rows : undefined)} onInvalid={onInvalid} />;
    case "ssn":
    case "ein":
    case "routing":
    case "account":
      return <SensitiveInput id={id} type={q.type} value={typeof value === "string" ? value : undefined} onChange={onChange} onInvalid={onInvalid} />;
    case "yes_no":
      return (
        <div className="flex gap-2" role="radiogroup" aria-labelledby={`${id}-label`}>
          {[true, false].map((v) => (
            <Button key={String(v)} type="button" role="radio" aria-checked={value === v} variant={value === v ? "default" : "outline"} size="sm" onClick={() => onChange(v)}>
              {v ? "Yes" : "No"}
            </Button>
          ))}
        </div>
      );
    case "choice":
      return (
        <select id={id} value={typeof value === "string" ? value : ""} onChange={(e) => onChange(e.target.value || undefined)} className="block h-9 w-full max-w-sm rounded-md border border-input bg-background px-2 text-sm">
          <option value="">Select an option</option>
          {q.options!.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      );
    case "count":
    case "number":
      return (
        <Input
          id={id}
          type="number"
          inputMode={q.type === "count" ? "numeric" : "decimal"}
          min={q.type === "count" ? 0 : undefined}
          max={q.type === "count" ? 99 : undefined}
          className="max-w-40"
          value={typeof value === "number" ? value : ""}
          onChange={(e) => onChange(e.target.value === "" ? undefined : Number(e.target.value))}
        />
      );
    case "money":
      return (
        <Input
          id={id}
          inputMode="decimal"
          placeholder="$0.00"
          className="max-w-48"
          value={moneyText}
          aria-invalid={moneyText.trim() !== "" && !isMoney(moneyText)}
          onChange={(e) => {
            setMoneyText(e.target.value);
            // Never save a different number than the one on screen: block saving until it's valid.
            if (e.target.value.trim() === "") {
              onInvalid(false);
              onChange(undefined);
            } else if (isMoney(e.target.value)) {
              onInvalid(false);
              onChange(parseDollars(e.target.value));
            } else onInvalid(true);
          }}
        />
      );
    case "date":
      return <Input id={id} type="date" className="max-w-48" value={typeof value === "string" ? value : ""} onChange={(e) => onChange(e.target.value || undefined)} />;
    case "long_text":
      return <Textarea id={id} rows={3} value={typeof value === "string" ? value : ""} onChange={(e) => onChange(e.target.value)} />;
    default:
      return <Input id={id} value={typeof value === "string" ? value : ""} onChange={(e) => onChange(e.target.value)} />;
  }
}

export function PortalOrganizerPage({ id }: { id: string }) {
  const { toast } = useToast();
  const [, navigate] = useLocation();
  const { data, isLoading, error, refetch } = useQuery<OrganizerView>({ queryKey: ["/api/portal/organizers/", id], staleTime: Infinity });
  const def = useMemo(() => (data ? organizerDefinitionSchema.parse(data.definition) : null), [data]);
  const [answers, setAnswers] = useState<Answers | null>(null);
  const [touched, setTouched] = useState<Set<string>>(new Set());
  const [dirty, setDirty] = useState(false);
  const [invalid, setInvalid] = useState<Set<string>>(new Set());
  const current = answers ?? data?.answers ?? {};
  const put = () => api.send("PUT", `/api/portal/organizers/${id}/answers`, { answers: current, filledAt: data?.fromReturn?.readAt ?? null });
  // The firm started answers from last year's return after this page opened: show them, with the client's own changes on top.
  const onConflict = async (err: Error) => {
    if (!(err instanceof ApiError) || err.code !== "organizer_changed") return;
    const fresh = (await refetch()).data;
    if (!fresh) return;
    const next = { ...fresh.answers };
    for (const k of touched) {
      if (current[k] === undefined) delete next[k];
      else next[k] = current[k]!;
    }
    setAnswers(next);
  };
  const save = useMutation({
    meta: { inlineError: true },
    mutationFn: put,
    onSuccess: () => setDirty(false),
    onError: onConflict,
  });
  const submit = useMutation({
    meta: { inlineError: true },
    onError: onConflict,
    mutationFn: async () => {
      await put();
      return api.send<{ created: number; reopened: number }>("POST", `/api/portal/organizers/${id}/submit`);
    },
    onSuccess: (r) => {
      toast({
        title: data?.kind === "intake" ? "Thank you. Your form has been submitted." : "Thank you. Your organizer has been submitted.",
        description: r.created + r.reopened ? `${r.created + r.reopened} item${r.created + r.reopened > 1 ? "s have" : " has"} been added to your to-do list. Please provide ${r.created + r.reopened > 1 ? "them" : "it"} at your earliest convenience.` : "No additional documents or information are needed at this time.",
      });
      navigate("/");
    },
  });
  // A withdrawn form (or a stale link) is not found.
  if (error) return <Empty>This form is no longer available. It may have been withdrawn by your firm; please contact your firm with any questions.</Empty>;
  if (isLoading || !data || !def) return <Loading />;
  if (data.status !== "open") {
    return (
      <>
        <PageHeader back={{ label: "home", href: "/" }} title={data.title} />
        <Empty>This {data.kind === "intake" ? "form" : "organizer"} has been submitted to your firm. If you need to change an answer, please send your firm a message.</Empty>
      </>
    );
  }
  const shown = new Set(visibleQuestions(def, current).map((q) => q.key));
  const missing = new Set(missingRequired(def, current));
  const answered = [...shown].filter((k) => current[k] !== undefined && current[k] !== "").length;
  const set = (k: string, v: AnswerValue | undefined) => {
    const next = { ...current };
    if (v === undefined) delete next[k];
    else next[k] = v;
    setAnswers(next);
    setTouched((t) => new Set(t).add(k));
    setDirty(true);
  };
  return (
    <>
      <PageHeader
        back={{ label: "home", href: "/" }}
        title={data.title}
        description={
          data.kind === "intake"
            ? "Your answers help your firm understand your situation before work begins. Select Save progress at any time to return and finish later."
            : "Your answers determine which documents and information your firm will request for your return. Select Save progress at any time to return and finish later."
        }
      />
      {data.fromReturn && (
        <p className="mb-4 rounded-lg border border-primary/30 bg-primary/5 px-4 py-3 text-sm">
          We started some answers from your {data.fromReturn.taxYear} tax return. Each one says so below; please check it and change anything that is different this year.
        </p>
      )}
      <div className="sticky top-0 z-10 -mx-1 mb-4 flex items-center gap-3 rounded-lg border border-border bg-background/95 px-4 py-2 backdrop-blur-sm">
        <Progress value={(answered / Math.max(shown.size, 1)) * 100} aria-label="Questions answered" className="h-2 flex-1" />
        <span className="text-xs text-muted-foreground">
          {answered} of {shown.size} answered
        </span>
        <Button
          size="sm"
          variant="outline"
          disabled={!dirty || save.isPending || invalid.size > 0}
          onClick={() => save.mutate()}
        >
          {save.isPending ? "Saving…" : dirty ? "Save progress" : "Progress saved"}
        </Button>
      </div>
      <div className="space-y-6">
        {def.sections.map((sec) => {
          const qs = sec.questions.filter((q) => shown.has(q.key));
          if (!qs.length) return null;
          return (
            <Section key={sec.key} title={sec.title}>
              <div className="space-y-5">
                {qs.map((q) => (
                  <div key={q.key} className="space-y-1.5">
                    <Label htmlFor={`q-${q.key}`} id={`q-${q.key}-label`} className="text-sm font-medium">
                      {fillYear(q.label, data.taxYear)}
                      {q.required && <span className="text-destructive"> *</span>}
                    </Label>
                    {q.help && <p className="text-xs text-muted-foreground">{fillYear(q.help, data.taxYear)}</p>}
                    {data.fromReturn?.hints[q.key] && (
                      <p className="text-xs text-primary">
                        {data.fromReturn.hints[q.key]} {q.type === "group" ? `Please list them for ${data.taxYear}.` : current[q.key] === undefined || current[q.key] === "" ? `Please answer for ${data.taxYear}.` : "Please confirm, or change it if this year is different."}
                      </p>
                    )}
                    <QuestionInput
                      q={q}
                      year={data.taxYear}
                      value={current[q.key]}
                      onChange={(v) => set(q.key, v)}
                      onInvalid={(bad) =>
                        setInvalid((prev) => {
                          const next = new Set(prev);
                          if (bad) next.add(q.key);
                          else next.delete(q.key);
                          return next;
                        })
                      }
                    />
                    {invalid.has(q.key) && q.type === "money" && <p className="text-xs text-destructive">Enter a dollar amount, such as 1,234.56.</p>}
                    {invalid.has(q.key) && q.type === "group" && <p className="text-xs text-destructive">Please correct the entries marked in red before saving.</p>}
                  </div>
                ))}
              </div>
            </Section>
          );
        })}
      </div>
      <div className="mt-6 flex flex-wrap items-center gap-3">
        <Button onClick={() => submit.mutate()} disabled={submit.isPending || missing.size > 0 || invalid.size > 0}>
          {data.kind === "intake" ? "Submit form" : "Submit organizer"}
        </Button>
        {missing.size > 0 && <span className="text-sm text-muted-foreground">{missing.size} required question{missing.size > 1 ? "s" : ""} (marked *) still to be answered</span>}
        {(save.error || submit.error) && <span className="text-sm text-destructive">{errorMessage(save.error ?? submit.error)}</span>}
      </div>
    </>
  );
}

// --- Refund tracker --------------------------------------------------------------------------

type RefundCard = {
  id: string;
  taxYear: number;
  jurisdiction: string;
  form: string;
  isAmended: boolean;
  filingStatus: string | null;
  kind: "refund" | "balance_due" | "none" | "pending";
  refundCents: number;
  balanceDueCents: number;
  acceptedOn: string | null;
  filedOn: string | null;
  received: boolean;
  refundReceivedOn: string | null;
  reportedMissing: boolean;
  checkFrom: string | null;
  expectedBy: string | null;
  tooEarly: boolean;
  canReportMissing: boolean;
  authority: { name: string; toolName: string; lookupUrl: string; appUrl: string | null; phone: string | null; inputs: string[]; notes: string[]; timingOfficial: boolean } | null;
  bankProduct: { partnerName: string | null; trackerUrl: string | null } | null;
};

const INPUT_LABELS: Record<string, string> = { ssn: "Your Social Security number (enter it yourself; it is not displayed here)", filing_status: "Filing status", refund_amount: "Exact refund amount", tax_year: "Tax year" };

function CopyValue({ label, value }: { label: string; value: string }) {
  const { toast } = useToast();
  return (
    <div className="flex items-center justify-between gap-2 rounded-md bg-muted/60 px-3 py-2">
      <span className="text-sm">
        <span className="text-muted-foreground">{label}:</span> <span className="font-semibold">{value}</span>
      </span>
      <Button
        size="sm"
        variant="ghost"
        aria-label={`Copy ${label}`}
        onClick={() => {
          navigator.clipboard.writeText(value);
          toast({ title: "Copied", description: `${label}: ${value}` });
        }}
      >
        <Copy className="h-4 w-4" />
      </Button>
    </div>
  );
}

/** A filed return with no refund to track (tax owed, neither, or figures not yet recorded): the client sees it was filed. */
function NoRefundCardView({ c }: { c: RefundCard }) {
  const where = c.jurisdiction === "federal" ? "Federal" : c.jurisdiction;
  return (
    <Section title={`${c.taxYear} ${where} return${c.isAmended ? " (amended)" : ""}`}>
      <div className="space-y-3">
        {c.kind === "pending" ? (
          <p className="text-sm font-medium">Filed {formatDate(c.filedOn)}; refund details will appear once your firm records them.</p>
        ) : (
          <>
            <p className="text-sm font-medium">{c.kind === "balance_due" ? `No refund: this return had a balance due of ${formatCents(c.balanceDueCents)}.` : "No refund or balance due on this return."}</p>
            <p className="text-sm text-muted-foreground">
              {c.acceptedOn ? `Filed ${formatDate(c.filedOn)} and accepted ${formatDate(c.acceptedOn)}.` : `Filed ${formatDate(c.filedOn)}.`}
            </p>
          </>
        )}
      </div>
    </Section>
  );
}

function RefundCardView({ c }: { c: RefundCard }) {
  if (c.kind !== "refund") return <NoRefundCardView c={c} />;
  return <RefundAmountCardView c={c} />;
}

function RefundAmountCardView({ c }: { c: RefundCard }) {
  const report = useMutation({ mutationFn: (path: string) => api.send("POST", `/api/portal/refunds/${c.id}/${path}`) });
  const a = c.authority;
  // Copy the amount as it appears on the return, with no $ or commas (returns rounded to whole dollars copy without cents).
  const amount = c.refundCents % 100 === 0 ? String(c.refundCents / 100) : (c.refundCents / 100).toFixed(2);
  return (
    <Section title={`${c.taxYear} ${c.jurisdiction === "federal" ? "Federal" : c.jurisdiction} refund${c.isAmended ? " (amended)" : ""}`}>
      <div className="space-y-3">
        <p className="text-3xl font-semibold">{formatCents(c.refundCents)}</p>
        {c.received ? (
          <p className="flex items-center gap-2 text-sm text-emerald-700 dark:text-emerald-300">
            <CheckCircle2 className="h-4 w-4" /> Received {formatDate(c.refundReceivedOn)}
          </p>
        ) : (
          <p className="text-sm text-muted-foreground">
            {c.acceptedOn ? `Accepted ${formatDate(c.acceptedOn)}.` : `Filed ${formatDate(c.filedOn)}.`}{" "}
            {c.expectedBy ? `Most refunds are issued by ${formatDate(c.expectedBy)}.` : "This agency does not publish a standard processing timeline."}
          </p>
        )}
        {a && !c.received && (
          <>
            {c.tooEarly && (
              <p className="flex items-center gap-2 rounded-md border border-accent/60 bg-accent/10 px-3 py-2 text-sm">
                <Clock className="h-4 w-4" /> It's too early to check. Status should be available from {formatDate(c.checkFrom)}.
              </p>
            )}
            <div className="space-y-2">
              <p className="text-sm font-medium">{a.toolName} will ask for:</p>
              {a.inputs.includes("refund_amount") && <CopyValue label="Exact refund amount" value={amount} />}
              {a.inputs.includes("filing_status") && c.filingStatus && <CopyValue label="Filing status" value={c.filingStatus} />}
              {a.inputs.includes("tax_year") && <CopyValue label="Tax year" value={String(c.taxYear)} />}
              {a.inputs.includes("ssn") && <p className="text-xs text-muted-foreground">{INPUT_LABELS.ssn}</p>}
            </div>
            <div className="flex flex-wrap gap-2">
              <a href={a.lookupUrl} target="_blank" rel="noreferrer noopener" className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90">
                Check with {a.name} <ExternalLink className="h-4 w-4" />
              </a>
              {a.appUrl && (
                <a href={a.appUrl} target="_blank" rel="noreferrer noopener" className="inline-flex items-center gap-1 rounded-md border border-border px-3 py-2 text-sm hover:bg-muted">
                  IRS2Go app <ExternalLink className="h-3.5 w-3.5" />
                </a>
              )}
            </div>
            {a.phone && (
              <p className="flex items-center gap-2 text-sm text-muted-foreground">
                <Phone className="h-4 w-4" /> {a.phone}
              </p>
            )}
            <ul className="list-disc space-y-1 pl-5 text-xs text-muted-foreground">
              {a.notes.map((n) => (
                <li key={n}>{n}</li>
              ))}
              {!a.timingOfficial && <li>Processing times vary; the official tool shows the latest status.</li>}
            </ul>
          </>
        )}
        {!a && !c.received && <p className="text-sm text-muted-foreground">Check your state tax agency's website, or contact your firm for assistance.</p>}
        {c.bankProduct && !c.received && (
          <p className="rounded-md border border-border px-3 py-2 text-sm">
            Your refund goes through {c.bankProduct.partnerName ?? "our bank partner"} first, which takes out the fees you agreed to before sending the rest to you.
            {c.bankProduct.trackerUrl && (
              <>
                {" "}
                <a href={c.bankProduct.trackerUrl} target="_blank" rel="noreferrer noopener" className="text-primary underline">
                  Check status with {c.bankProduct.partnerName ?? "the bank"}
                </a>
                .
              </>
            )}
          </p>
        )}
        {!c.received && (
          <div className="flex flex-wrap gap-2 border-t border-border pt-3">
            <Button size="sm" variant="outline" onClick={() => report.mutate("received")}>
              Refund received
            </Button>
            {c.reportedMissing ? (
              <span className="text-sm text-muted-foreground">Your inquiry has been received. Your firm is following up.</span>
            ) : (
              c.canReportMissing && (
                <Button size="sm" variant="ghost" onClick={() => report.mutate("not-received")}>
                  Report refund not received
                </Button>
              )
            )}
          </div>
        )}
      </div>
    </Section>
  );
}

export function PortalRefundsPage() {
  const { data, isLoading } = useQuery<RefundCard[]>({ queryKey: ["/api/portal/refunds"] });
  return (
    <>
      <PageHeader title="Refund Status" description="Refund status for your filed returns, with links to the IRS and state refund tools." />
      {isLoading ? (
        <Loading />
      ) : !data?.length ? (
        <Empty>Refund information appears here after your return is filed.</Empty>
      ) : (
        <div className="grid gap-6 lg:grid-cols-2">
          {data.map((c) => (
            <RefundCardView key={c.id} c={c} />
          ))}
        </div>
      )}
    </>
  );
}

// --- Fee taken from the refund -----------------------------------------------------------------

/** The client reviews and approves the fee to be taken from their refund (or says what's wrong). */
export function PortalFeeApprovalPage({ id }: { id: string }) {
  const key = ["/api/portal/fee-approvals/", id];
  const { data, isLoading, error, refetch } = useQuery<FeeApproval>({ queryKey: key });
  const [name, setName] = useState("");
  const [agree, setAgree] = useState(false);
  const approve = useMutation({ meta: { inlineError: true }, mutationFn: () => api.send("POST", `/api/portal/fee-approvals/${id}/approve`, { name, totalCents: data!.totalCents }), onSuccess: () => refetch(), onError: () => refetch() });
  const decline = useMutation({
    meta: { inlineError: true },
    mutationFn: (note: string) => api.send("POST", `/api/portal/fee-approvals/${id}/decline`, { note }),
    onSuccess: () => refetch(),
  });
  if (isLoading) return <Loading />;
  if (error || !data)
    return (
      <>
        <h1 className="sr-only">Approve your fee</h1>
        <Empty>This fee isn't available. It may have been changed by your firm; please check your messages or contact your firm.</Empty>
      </>
    );
  const from = data.partnerName ? ` by ${data.partnerName}` : "";
  return (
    <>
      <PageHeader back={{ label: "home", href: "/" }} title="Approve your fee" description={data.engagementName ?? undefined} />
      <Section title="The fee">
        <table className="w-full text-sm">
          <tbody>
            {data.lines.map((l, i) => (
              <tr key={i} className="border-b border-border/60">
                <td className="py-2">
                  {l.description}
                  {l.quantity > 1 && <span className="text-muted-foreground"> × {l.quantity}</span>}
                </td>
                <td className="tabular py-2 text-right">{formatCents(l.amountCents)}</td>
              </tr>
            ))}
            <tr className="font-semibold">
              <td className="pt-3">Total, taken from your refund</td>
              <td className="tabular pt-3 text-right">{formatCents(data.totalCents)}</td>
            </tr>
          </tbody>
        </table>
        {data.memo && <p className="mt-3 text-sm text-muted-foreground">{data.memo}</p>}
        <p className="mt-4 rounded-md bg-muted/60 px-3 py-2 text-sm text-muted-foreground">
          When your refund is issued, this amount is deducted{from} and the rest of your refund is sent to you. Nothing is charged to your card or bank account.
        </p>
        {data.approvedAt ? (
          <p className="mt-5 flex items-center gap-2 text-sm text-emerald-700 dark:text-emerald-300">
            <CheckCircle2 className="h-4 w-4" /> Approved by {data.approvedName} on {formatDate(data.approvedAt.slice(0, 10))}. Thank you.
          </p>
        ) : data.declinedAt ? (
          <p className="mt-5 text-sm text-muted-foreground">You told your firm the fee needs a change. They'll be in touch.</p>
        ) : (
          <form
            className="mt-5 space-y-3"
            onSubmit={(e) => {
              e.preventDefault();
              approve.mutate();
            }}
          >
            <label className="flex items-start gap-2 text-sm">
              <input type="checkbox" className="mt-0.5 h-4 w-4" checked={agree} onChange={(e) => setAgree(e.target.checked)} />
              <span>
                I approve the fee of {formatCents(data.totalCents)} to be taken from my refund{from}.
              </span>
            </label>
            <div className="max-w-sm space-y-1">
              <Label htmlFor="fee-name">Your full name</Label>
              <Input id="fee-name" value={name} onChange={(e) => setName(e.target.value)} autoComplete="name" />
            </div>
            <div className="flex flex-wrap gap-2">
              <Button type="submit" disabled={!agree || !name.trim() || approve.isPending}>
                <BadgeCheck className="mr-2 h-4 w-4" /> Approve the fee
              </Button>
              <Button
                type="button"
                variant="outline"
                disabled={decline.isPending}
                onClick={async () => {
                  const note = await askText({ title: "What should change?", description: "Your firm will see your note and get back to you.", label: "Your note", required: true, confirmLabel: "Send to my firm" });
                  if (note?.trim()) decline.mutate(note.trim());
                }}
              >
                Something isn't right
              </Button>
            </div>
            {(approve.error || decline.error) && <p className="text-sm text-destructive">{errorMessage(approve.error ?? decline.error)}</p>}
          </form>
        )}
      </Section>
    </>
  );
}

/** On the signing page: fees still to approve, so reviewing the return and approving its fee happen together. */
export function FeeApprovalReminder() {
  const { data } = useQuery<FeeApproval[]>({ queryKey: ["/api/portal/fee-approvals"] });
  const mine = data ?? [];
  if (!mine.length) return null;
  return (
    <div className="mb-4 space-y-2">
      {mine.map((f) => (
        <Link key={f.id} href={`/fees/${f.id}`} className="flex items-center gap-3 rounded-lg border border-primary/40 bg-primary/5 px-4 py-3 text-sm hover:bg-primary/10">
          <BadgeCheck className="h-4 w-4 shrink-0 text-primary" />
          <span className="flex-1">
            Please also approve your fee of <span className="font-semibold">{formatCents(f.totalCents)}</span>, to be taken from your refund.
          </span>
          <span className="font-medium text-primary">Review</span>
        </Link>
      ))}
    </div>
  );
}
